import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, channelName, Channel, Topic } from '../lib/api'
import HudLoader from '../components/HudLoader'
import { LanguageSelect, MarketMultiSelect, csvToArr } from '../lib/i18n'
import {
  Plus,
  Edit,
  Trash2,
  Brain,
  Loader2,
  Sparkles,
  FileText,
  Check,
  X,
} from 'lucide-react'
import { format } from 'date-fns'

const STATUS_COLORS: Record<string, string> = {
  discovered: 'badge-info',
  researched: 'badge-primary',
  approved: 'badge-success',
  rejected: 'badge-danger',
  in_production: 'badge-warning',
  published: 'badge-success',
}

export default function Topics() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null)
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keywords: '',
    category: 'technology',
    source: '',
    source_url: '',
    language: 'en',
    target_countries: 'US,IN,GB',
    priority: 0,
  })

  const { data: topics, isLoading } = useQuery({
    queryKey: ['topics', selectedChannel],
    queryFn: () => api.get<Topic[]>(`/topics${selectedChannel ? `?channel_id=${selectedChannel}` : ''}`),
  })

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<Channel[]>('/channels'),
  })

  // Channel ke hisab se Discover category auto — galat-niche topics ka default trap khatam.
  useEffect(() => {
    const ch = channels?.find((c) => c.id === selectedChannel)
    if (!ch) return
    if (ch.made_for_kids || ch.niche === 'kids') setDiscoverCategory('kids')
    else if (ch.niche === 'space') setDiscoverCategory('space')
    else if (ch.niche === 'gaming') setDiscoverCategory('gaming')
    else if (ch.niche === 'education') setDiscoverCategory('education')
    else setDiscoverCategory('technology')
  }, [selectedChannel, channels])

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post<Topic>('/topics', { ...data, channel_id: selectedChannel }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
      setShowModal(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) => api.patch<Topic>(`/topics/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
      setEditingTopic(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/topics/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics'] }),
  })

  const researchMutation = useMutation({
    mutationFn: (id: string) => api.post<Topic>(`/topics/${id}/research`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics'] }),
  })

  const discoverMutation = useMutation({
    mutationFn: ({ channel_id, region, category }: { channel_id: string; region: string; category: string }) =>
      api.post<Topic[]>('/topics/discover', { channel_id, region, category }),
    onSuccess: (topics) => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
      if (!topics?.length) alert('Naye topics nahi mile — sab pehle se list me hain.')
    },
    onError: (e: any) => alert(`Discover fail: ${e.message}`),
  })
  const [discoverRegion, setDiscoverRegion] = useState('IN')
  const [discoverCategory, setDiscoverCategory] = useState('technology')

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch<Topic>(`/topics/${id}`, { status: 'approved' } as any),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics'] }),
    onError: (e: any) => alert(`Approve nahi hua: ${e.message}`),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingTopic) {
      updateMutation.mutate({ id: editingTopic.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      keywords: '',
      category: 'technology',
      source: '',
      source_url: '',
      language: 'en',
      target_countries: 'US,IN,GB',
      priority: 0,
    })
  }

  const openCreate = () => {
    if (!selectedChannel) return
    setEditingTopic(null)
    resetForm()
    setShowModal(true)
  }

  const openEdit = (topic: Topic) => {
    setEditingTopic(topic)
    setFormData({
      title: topic.title,
      description: topic.description || '',
      keywords: topic.keywords || '',
      category: topic.category || 'technology',
      source: topic.source || '',
      source_url: topic.source_url || '',
      language: topic.language,
      target_countries: topic.target_countries || 'US,IN,GB',
      priority: topic.priority,
    })
    setShowModal(true)
  }

  if (isLoading) return <HudLoader text="Loading topics" />

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Topics</h1>
          <p>Discover, research, and manage content topics</p>
        </div>
        <div className="header-actions">
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="select"
            style={{ minWidth: 200 }}
          >
            <option value="">All Channels</option>
            {channels?.map((c: any) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={openCreate} disabled={!selectedChannel}>
            <Plus size={18} />
            Add Topic
          </button>
          {selectedChannel && (
            <>
              <select
                value={discoverCategory}
                onChange={(e) => setDiscoverCategory(e.target.value)}
                className="select"
                title="Topic category"
              >
                <option value="technology">Technology</option>
                <option value="kids">Kids & Family</option>
                <option value="space">Space & Science</option>
                <option value="education">Education</option>
                <option value="gaming">Gaming</option>
                <option value="music">Music</option>
              </select>
              <select
                value={discoverRegion}
                onChange={(e) => setDiscoverRegion(e.target.value)}
                className="select"
                title="Region"
              >
                <option value="IN">India</option>
                <option value="US">USA</option>
                <option value="GB">UK</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="BR">Brazil</option>
                <option value="JP">Japan</option>
                <option value="AU">Australia</option>
              </select>
              <button className="btn btn-secondary" onClick={() => discoverMutation.mutate({ channel_id: selectedChannel, region: discoverRegion, category: discoverCategory })} disabled={discoverMutation.isPending}>
                <Sparkles size={18} />
                {discoverMutation.isPending ? 'Finding...' : 'Discover Trends'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Topic</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Category</th>
                <th>Language</th>
                <th>Target Countries</th>
                <th>Priority</th>
                <th>Source</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {topics?.map((topic) => (
                <tr key={topic.id}>
                  <td>
                    <div>
                      <span className="topic-title">{topic.title}</span>
                      {topic.description && <span className="topic-desc">{topic.description}</span>}
                    </div>
                  </td>
                  <td>
                    <div>
                      <span className="topic-title">{channelName(channels, topic.channel_id)}</span>
                      <span className="topic-desc">{topic.channel_id.slice(0, 8)}…</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_COLORS[topic.status] || 'badge-secondary'}`}>
                      {topic.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{topic.category}</td>
                  <td>{topic.language}</td>
                  <td>{topic.target_countries || 'Global'}</td>
                  <td>{topic.priority}</td>
                  <td>{topic.source || 'Manual'}</td>
                  <td>{format(new Date(topic.created_at), 'MMM dd, yyyy')}</td>
                  <td>
                    <div className="action-menu">
                      {(topic.status === 'discovered' || topic.status === 'researched') && (
                        <button className="icon-btn" onClick={() => approveMutation.mutate(topic.id)} disabled={approveMutation.isPending} title="Approve (auto-pilot isi ko uthayega)">
                          <Check size={16} />
                        </button>
                      )}
                      <button className="icon-btn" onClick={() => researchMutation.mutate(topic.id)} disabled={researchMutation.isPending || topic.status === 'researched'} title="Research">
                        {researchMutation.isPending && researchMutation.variables === topic.id ? <Loader2 size={16} className="spin" /> : <Brain size={16} />}
                      </button>
                      <button className="icon-btn" onClick={() => openEdit(topic)} title="Edit">
                        <Edit size={16} />
                      </button>
                      <button className="icon-btn danger" onClick={() => { if (window.confirm(`"${topic.title}" delete karo? Live job wala topic delete nahi hoga.`)) deleteMutation.mutate(topic.id) }} disabled={deleteMutation.isPending} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!topics?.length && (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No topics yet</h3>
            <p>Create topics manually or discover trending topics</p>
            <button className="btn btn-primary" onClick={openCreate} disabled={!selectedChannel}>
              <Plus size={18} />
              Add Topic
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTopic ? 'Edit Topic' : 'Add Topic'}</h2>
              <button className="icon-btn" onClick={() => { setShowModal(false); resetForm(); }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Building AI Agents with LangGraph"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Complete tutorial on building autonomous AI agents..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="keywords">Keywords (comma-separated)</label>
                <input
                  id="keywords"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="langgraph, ai agents, python, tutorial"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="technology">Technology</option>
                    <option value="programming">Programming</option>
                    <option value="ai">AI/Machine Learning</option>
                    <option value="webdev">Web Development</option>
                    <option value="devops">DevOps</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="language">Language (50+ supported)</label>
                  <LanguageSelect
                    id="language"
                    value={formData.language}
                    onChange={(v) => setFormData({ ...formData, language: v })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="target_countries">Target Countries — tick jo chahiye (40+ markets)</label>
                <MarketMultiSelect
                  value={csvToArr(formData.target_countries)}
                  onChange={(v) => setFormData({ ...formData, target_countries: v.join(',') })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="source">Source</label>
                  <input
                    id="source"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="google_trends"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="source_url">Source URL</label>
                  <input
                    id="source_url"
                    type="url"
                    value={formData.source_url}
                    onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                    placeholder="https://trends.google.com/..."
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <input
                  id="priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  min={0}
                  max={100}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingTopic ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}